import { useEffect, useRef, useState } from 'react';
import { X, RotateCcw, Download, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as monaco from 'monaco-editor';
import { useTheme } from '@/contexts/theme-context';

interface DiffViewerProps {
  filePath: string;
  originalContent: string;
  modifiedContent: string;
  originalLabel?: string;
  modifiedLabel?: string;
  onClose: () => void;
  onRevert?: () => void;
  viewMode?: 'inline' | 'side-by-side';
}

export function DiffViewer({
  filePath,
  originalContent,
  modifiedContent,
  originalLabel = 'Original',
  modifiedLabel = 'Modified',
  onClose,
  onRevert,
  viewMode: initialViewMode = 'side-by-side'
}: DiffViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const diffEditorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);
  const [viewMode, setViewMode] = useState<'inline' | 'side-by-side'>(initialViewMode);
  const [copied, setCopied] = useState(false);
  const { theme } = useTheme();

  // Get file language from extension
  const getLanguage = (path: string): string => {
    const ext = path.split('.').pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      'ts': 'typescript',
      'tsx': 'typescript',
      'js': 'javascript',
      'jsx': 'javascript',
      'json': 'json',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'md': 'markdown',
      'py': 'python',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c',
      'go': 'go',
      'rs': 'rust',
      'php': 'php',
      'rb': 'ruby',
      'sh': 'shell',
      'yaml': 'yaml',
      'yml': 'yaml',
      'xml': 'xml',
      'sql': 'sql',
    };
    return languageMap[ext || ''] || 'plaintext';
  };

  // Initialize diff editor
  useEffect(() => {
    if (!containerRef.current) return;

    const language = getLanguage(filePath);
    
    // Create models
    const originalModel = monaco.editor.createModel(originalContent, language);
    const modifiedModel = monaco.editor.createModel(modifiedContent, language);

    // Determine theme based on current theme
    let configuredTheme = theme;
    if (theme === 'system') {
      configuredTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    // Create diff editor
    const diffEditor = monaco.editor.createDiffEditor(containerRef.current, {
      automaticLayout: true,
      renderSideBySide: initialViewMode === 'side-by-side',
      readOnly: true,
      theme: configuredTheme === 'dark' ? 'v1-dev-dark' : 'v1-dev',
      minimap: { enabled: false },
      fontSize: 13,
      scrollBeyondLastLine: false,
      renderOverviewRuler: true,
      diffWordWrap: 'on',
      ignoreTrimWhitespace: false,
    });

    diffEditor.setModel({
      original: originalModel,
      modified: modifiedModel
    });

    diffEditorRef.current = diffEditor;

    return () => {
      diffEditor.dispose();
      originalModel.dispose();
      modifiedModel.dispose();
    };
  }, [filePath, originalContent, modifiedContent, theme, initialViewMode]);

  // Update view mode
  useEffect(() => {
    if (diffEditorRef.current) {
      diffEditorRef.current.updateOptions({
        renderSideBySide: viewMode === 'side-by-side'
      });
    }
  }, [viewMode]);

  const handleCopyDiff = async () => {
    // Create a simple diff text representation
    const diffText = `--- ${originalLabel}\n+++ ${modifiedLabel}\n\n${originalContent}\n\n---\n\n${modifiedContent}`;
    await navigator.clipboard.writeText(diffText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadDiff = () => {
    const diffText = `--- ${originalLabel}\n+++ ${modifiedLabel}\n\n${originalContent}\n\n---\n\n${modifiedContent}`;
    const blob = new Blob([diffText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filePath.split('/').pop()}.diff`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Compare: {filePath}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {originalLabel} ↔ {modifiedLabel}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('side-by-side')}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  viewMode === 'side-by-side'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                Side by Side
              </button>
              <button
                onClick={() => setViewMode('inline')}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  viewMode === 'inline'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                Inline
              </button>
            </div>

            {/* Actions */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyDiff}
              className="text-xs"
            >
              {copied ? (
                <>
                  <Check className="size-3 mr-1" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="size-3 mr-1" />
                  Copy
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadDiff}
              className="text-xs"
            >
              <Download className="size-3 mr-1" />
              Download
            </Button>

            {onRevert && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRevert}
                className="text-xs"
              >
                <RotateCcw className="size-3 mr-1" />
                Revert to Original
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="size-5" />
            </Button>
          </div>
        </div>

        {/* Diff Editor */}
        <div className="flex-1 overflow-hidden">
          <div ref={containerRef} className="h-full w-full" />
        </div>

        {/* Footer with Legend */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-200 dark:bg-green-900 border border-green-400 dark:border-green-600 rounded" />
                <span>Additions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-200 dark:bg-red-900 border border-red-400 dark:border-red-600 rounded" />
                <span>Deletions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-200 dark:bg-blue-900 border border-blue-400 dark:border-blue-600 rounded" />
                <span>Modifications</span>
              </div>
            </div>
            <span>
              Use Ctrl+F to search within the diff
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

